import React, { useState, useEffect, useRef } from 'react';
import { useOnResize } from '../../../../../../../../../Foundation/React/code/Hooks/UseOnResize.jsx';
import { Image, ProductData } from '../Shared/Interfaces.ts';
import { TrackCompare, TrackModalClose, TrackModalCloseFit, TrackModalOpen } from '../Shared/Tracking.ts';
import { getDeviceParameterData, getSlotArray } from '../Shared/Helpers.ts';
import { DeviceParameters } from '../Shared/Enum/DeviceParameters.ts';
import ProductCard from './Components/Product/ProductCard.tsx';
import ProductSlot from './Components/ProductSlot/ProductSlot.tsx';

import './ComparisonModal.less';

const ComparisonModal = () => {
  const CHECKBOX_CHECKED_EVENT: string = 'comparisonCheckboxChecked';
  const FA_OPEN_EVENT: string = 'faOpened';

  const ref = useRef<HTMLDivElement | null>(null);

  const [products, setProducts] = useState<ProductData[]>([]);
  const [productsForScreenSize, setProductsForScreenSize] = useState<ProductData[]>([]);
  const [active, setActive] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [width, height] = useOnResize();

  const translations = window._THULEDATA.ComparisonModal.Translations;
  const cookieBotWidget: HTMLElement | null = document.getElementById('CookiebotWidget');

  useEffect(() => {
    let waitForCookiebot = loadProducts();

    if (waitForCookiebot) {
      let attempts = 0;
      let maxAttempts = 20;
      let cookiebotInterval = setInterval(() => {
        let cookieBotWidget: HTMLElement | null = document.getElementById('CookiebotWidget');
        if (cookieBotWidget) {
          cookieBotWidget?.classList.add('d-none');
          clearInterval(cookiebotInterval);
        } else if (attempts === maxAttempts) {
          clearInterval(cookiebotInterval);
        }
      }, 50);
    }

    document.addEventListener(CHECKBOX_CHECKED_EVENT, () => {
      loadProducts();
    });

    document.addEventListener(FA_OPEN_EVENT, () => {
      closeAndClear(true);
    });

    setHasLoaded(true);

    return () => {
      document.removeEventListener(CHECKBOX_CHECKED_EVENT, () => {
        loadProducts();
      });

      document.removeEventListener(FA_OPEN_EVENT, () => {
        closeAndClear(true);
      });
    };
  }, []);

  useEffect(() => {
    let currentDeviceParameters: DeviceParameters = getDeviceParameterData();
    let numberToTake: number = currentDeviceParameters === DeviceParameters.Desktop ? 4 : 2;
    let productsForScreenSize = products.slice(0, numberToTake);
    setProductsForScreenSize(productsForScreenSize);
  }, [width, height, products]);

  useEffect(() => {
    setActive(products.length > 1);
  }, [products]);

  useEffect(() => {
    if (active) {
      TrackModalOpen();
      cookieBotWidget?.classList.add('d-none');
    } else if (!active && hasLoaded) {
      cookieBotWidget?.classList.remove('d-none');
    }
  }, [active]);

  const loadProducts = (): boolean => {
    let subcategoryId = window._THULEDATA.ComparisonModal.SubcategoryId;
    let productIdsJson = localStorage.getItem(subcategoryId);

    let productIds: string[] = !productIdsJson ? [] : JSON.parse(productIdsJson);

    // Dedupe so a single selection can never fill more than its own slot.
    let uniqueIds = Array.from(new Set(productIds));

    let productData: ProductData[] = [];

    // The PLP already renders each variant's data into the grouped card's
    // data-variants blob, so resolve the stored id against it - no extra fetch.
    let findVariant = (pimId: string) => {
      let wrappers = document.querySelectorAll('[data-variants]:not([data-variants=""])');
      for (let i = 0; i < wrappers.length; i++) {
        let raw = (wrappers[i] as HTMLElement).dataset.variants || '';
        let variants: Array<{ PimId: string; Title: string; WebTitle1: string; ImageSrc: string }> = [];
        try {
          variants = JSON.parse(raw);
        } catch {
          variants = [];
        }
        let match = variants.find((v) => v && v.PimId === pimId);
        if (match) return match;
      }
      return null;
    };

    uniqueIds.forEach((element) => {
      let variant = findVariant(element);
      if (variant) {
        // Match the PLP card: GroupedProductCard renders variant.Title (WebTitle1,
        // with the per-SKU product name as fallback).
        let webTitle1 = variant.Title || variant.WebTitle1;
        let image = { src: variant.ImageSrc, alt: webTitle1 } as Image;
        productData.push({ productId: element, webTitle1: webTitle1, image: image } as ProductData);
        return;
      }

      // Ungrouped product: read the single matching list item (data-productname is WebTitle1).
      let product = document.querySelector(`.product-list-item[data-productid="${element}"]`);
      if (product) {
        let imageSrc = product.querySelector('img')?.getAttribute('src');
        let imageAlt = product.querySelector('img')?.getAttribute('alt');

        let image = { src: imageSrc, alt: imageAlt } as Image;
        productData.push({
          productId: product.getAttribute('data-productid'),
          webTitle1: product.getAttribute('data-productname'),
          image: image,
        } as ProductData);
      }
    });

    setProducts(productData);

    return productData.length > 0;
  };

  const compare = () => {
    let comparisonData = {
      SubcategoryId: window._THULEDATA.ComparisonModal.SubcategoryId,
      ProductIds: products.map((product) => product.productId),
    };

    TrackCompare(products);

    let base64data = window.btoa(JSON.stringify(comparisonData));
    let comparisonPageUrl = window._THULEDATA.ComparisonModal.ComparePageUrl;
    window.location.href = `${comparisonPageUrl}?data=${base64data}`;
  };

  const closeAndClear = (isFit?: boolean) => {
    let activeModal = ref.current && ref.current.classList.contains('active');

    if (activeModal && isFit) TrackModalCloseFit();
    if (activeModal && !isFit) TrackModalClose();

    setProducts([]);
    localStorage.removeItem(window._THULEDATA.ComparisonModal.SubcategoryId);
    document.dispatchEvent(new Event('comparisonModalProductRemoved'));
  };

  return (
    <div className={`comparison-modal ${active ? 'active' : ''}`} ref={ref}>
      <div className="modal-header">
        <div className="d-flex flex-column d-lg-block">
          <h4 className="me-4">{translations.CompareProducts}</h4>
          <p className="paragraph inline-desktop-only">{translations.UpToFourProducts}</p>
          <p className="paragraph d-block d-lg-none">{translations.UpToTwoProducts}</p>
        </div>
        <div>
          <p
            className="close-button paragraph--s text-decoration-underline"
            onClick={() => {
              closeAndClear();
            }}
          >
            {translations.Close}
          </p>
          <button
            className="btn thule-btn-compare m-0 ms-9 inline-desktop-only"
            disabled={products.length < 2}
            onClick={compare}
          >
            {translations.Compare}
          </button>
        </div>
      </div>
      <div className="product-slots">
        {productsForScreenSize.map((product, index) => (
          <ProductCard product={product} setProducts={setProducts} key={index} />
        ))}
        {getSlotArray(productsForScreenSize.length)
          .splice(productsForScreenSize.length)
          .map((_, index) => (
            <ProductSlot translation={translations.NoProductSelected} key={index} />
          ))}
      </div>
      <div className="mobile-button-container d-block d-lg-none">
        <button className="btn thule-btn-compare w-100 mt-7" disabled={products.length < 2} onClick={compare}>
          {translations.Compare}
        </button>
      </div>
    </div>
  );
};

export default ComparisonModal;
